import { ReactNode, useMemo, useState } from 'react';
import { inventoryState } from '@/stores/inventory.store';
import { IBaseItem } from '@/utils/types/Items';
import FluentSelect from '@/components/UI/FluentSelect';
import { rarityColor } from '@/utils/rarity';
import { iconBackgroundStyle } from '@/utils/iconBackground';

type Sort = 'high' | 'low' | 'az';

const SORT_OPTIONS = [
    { value: 'high' as const, label: 'High to low' },
    { value: 'low' as const, label: 'Low to high' },
    { value: 'az' as const, label: 'A → Z' },
];

interface PickerItem {
    id: number | bigint;
    name: string;
    priceUsd: number;
    colorHex?: string | null;
    icon?: string;
    iconBackground?: string | null;
}

interface Props {
    items: PickerItem[];
    selectedItemIds: inventoryState['selectedItemIds'];
    setSelectedById: (id: number | bigint) => void;
    maxItems: number;
    loading?: boolean;
    onRefresh?: () => void;
    footer: ReactNode;
}

const CoinflipInventoryPicker = ({
    items,
    selectedItemIds,
    setSelectedById,
    maxItems,
    loading,
    onRefresh,
    footer,
}: Props) => {
    const [sort, setSort] = useState<Sort>('high');
    const [search, setSearch] = useState('');

    const filtered = useMemo(() => {
        let out = items.slice();
        if (search) {
            const s = search.toLowerCase();
            out = out.filter((it) => it.name.toLowerCase().includes(s));
        }
        if (sort === 'high') out.sort((a, b) => b.priceUsd - a.priceUsd);
        else if (sort === 'low') out.sort((a, b) => a.priceUsd - b.priceUsd);
        else out.sort((a, b) => a.name.localeCompare(b.name));
        return out;
    }, [items, sort, search]);

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                <span className="font-chat-msg text-[13.5px] text-text_2">
                    Choose the items you’d like to deposit
                </span>
                <span
                    className="font-chat-nick inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-[0.06em]"
                    style={{
                        color: '#b7a6ff',
                        border: '1px solid color-mix(in srgb, #8b6dff 38%, transparent)',
                        background: 'color-mix(in srgb, #8b6dff 12%, transparent)',
                    }}
                >
                    MAX {maxItems}
                </span>
            </div>

            <div className="flex justify-between items-center mb-3 gap-3 max-md:flex-wrap">
                <FluentSelect<Sort>
                    value={sort}
                    options={SORT_OPTIONS}
                    onChange={setSort}
                    minWidth={170}
                />
                <div className="flex items-center gap-2.5">
                    {onRefresh && (
                        <button
                            onClick={onRefresh}
                            aria-label="Refresh"
                            className="w-10 h-10 rounded-fl grid place-items-center text-text_2 bg-layer_2 border border-stroke hover:bg-layer_3 hover:text-text_main hover:border-stroke_strong transition-colors"
                        >
                            <svg viewBox="0 0 24 24" className="w-4 h-4 stroke-current fill-none [stroke-width:2.2] [stroke-linecap:round]">
                                <path d="M21 12a9 9 0 1 1-3.5-7.1" />
                                <path d="M21 4v5h-5" />
                            </svg>
                        </button>
                    )}
                    <div className="flex items-center bg-layer_2 border border-stroke rounded-fl pl-4 pr-1 focus-within:border-accent focus-within:bg-layer_3 transition-colors flex-1 min-w-0">
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search…"
                            className="bg-transparent outline-none text-text_main py-2.5 px-2 text-sm w-[220px] max-md:w-full min-w-0 placeholder:text-text_4"
                        />
                        <button className="w-8 h-8 grid place-items-center text-text_3 hover:bg-layer_3 hover:text-text_main rounded-md">
                            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2]">
                                <circle cx="11" cy="11" r="7" />
                                <path d="m21 21-4.3-4.3" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <div
                className="fluent-scrollbar grid gap-2 overflow-y-auto flex-1 min-h-0 pr-1.5 pt-2 content-start [scrollbar-gutter:stable] max-md:[grid-template-columns:repeat(auto-fill,minmax(88px,1fr))]"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))' }}
            >
                {loading && (
                    <div className="col-span-full text-center py-12 text-text_3 text-sm">
                        Loading inventory…
                    </div>
                )}
                {!loading && filtered.length === 0 && (
                    <div className="col-span-full text-center py-12 text-text_3 text-sm">
                        No items match.
                    </div>
                )}
                {/* Never render items while a (re)load is in flight: the shared
                    inventory store may still hold the previous view's list
                    (e.g. PRIZE winnings from the Withdraw page) until the
                    filtered fetch resolves. */}
                {!loading && filtered.map((it) => {
                    const selected = selectedItemIds.has(it.id);
                    const rarity = rarityColor(it.colorHex);
                    return (
                        <div
                            key={String(it.id)}
                            onClick={() => setSelectedById(it.id)}
                            data-tip="1"
                            data-name={it.name}
                            data-price={it.priceUsd}
                            data-rarity={rarity}
                            data-image={it.icon}
                            data-icon-background={it.iconBackground ?? ''}
                            style={{ ['--rarity' as string]: rarity } as React.CSSProperties}
                            className={`group relative overflow-hidden bg-layer_2 border rounded-fl px-2 pt-3 pb-2.5 flex flex-col items-center cursor-pointer h-[146px] max-md:h-[120px] transition-all hover:-translate-y-0.5 ${
                                selected
                                    ? 'border-[#3ad6ff]/60 bg-[rgba(58,214,255,0.07)] shadow-[0_0_0_1.5px_#3ad6ff,0_0_26px_-2px_rgba(139,109,255,0.55)]'
                                    : 'border-stroke hover:[border-color:var(--rarity)] hover:shadow-[0_10px_24px_-10px_var(--rarity)]'
                            }`}
                        >
                            {/* rarity wash */}
                            <span
                                aria-hidden
                                className="absolute inset-0 pointer-events-none opacity-70"
                                style={{
                                    background: `radial-gradient(circle at 50% 26%, color-mix(in srgb, ${rarity} 16%, transparent), transparent 70%)`,
                                }}
                            />
                            <span
                                className="absolute top-1.5 left-1.5 w-1.5 h-1.5 rounded-full pointer-events-none z-[1]"
                                style={{ background: rarity, boxShadow: `0 0 6px ${rarity}` }}
                            />
                            {it.icon ? (
                                <img
                                    src={it.icon}
                                    alt=""
                                    className="relative w-[66px] h-[66px] max-md:w-[50px] max-md:h-[50px] object-contain mb-1.5 transition-transform group-hover:scale-105"
                                    style={{ filter: `drop-shadow(0 4px 8px rgba(0,0,0,0.4))`, ...iconBackgroundStyle(it.iconBackground) }}
                                    draggable={false}
                                />
                            ) : (
                                <div className="relative w-[66px] h-[66px] max-md:w-[50px] max-md:h-[50px] bg-layer_3 rounded mb-1.5" />
                            )}
                            <div className="font-chat-nick relative text-[13.5px] max-md:text-[12px] font-extrabold text-white tabular-nums">
                                ${it.priceUsd.toFixed(2)}
                            </div>
                            <div className="relative text-[11px] max-md:text-[10px] text-text_3 text-center truncate w-full mt-0.5">
                                {it.name}
                            </div>
                            {/* rarity base bar */}
                            <span
                                aria-hidden
                                className="absolute bottom-0 inset-x-0 h-[2.5px] transition-opacity"
                                style={{
                                    background: `linear-gradient(90deg, transparent, ${rarity}, transparent)`,
                                    opacity: selected ? 1 : 0.45,
                                }}
                            />
                            {selected && (
                                <div
                                    className="absolute top-1.5 right-1.5 w-4 h-4 rounded-[5px] grid place-items-center text-white text-[11px] font-bold z-[1]"
                                    style={{
                                        background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                                        boxShadow: '0 0 10px rgba(58,214,255,0.65)',
                                    }}
                                >
                                    ✓
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {footer}
        </div>
    );
};

export default CoinflipInventoryPicker;

export function toPickerItem(item: IBaseItem & { assetId?: string }): PickerItem {
    return {
        id: item.assetId ? BigInt(item.assetId) : (item.id as number),
        name: item.name,
        priceUsd: item.priceUsd,
        colorHex: item.colorHex,
        icon: item.icon,
        iconBackground: item.iconBackground,
    };
}
